'use client';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';

import { Editor, EditorProps, OnMount } from '@monaco-editor/react';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

import { Loader } from '../display/loader';

import type * as Monaco from 'monaco-editor';

type MonacoType = typeof Monaco;
type MonacoEditor = Monaco.editor.IStandaloneCodeEditor;

interface Props extends Omit<EditorProps, 'value' | 'onChange'> {
  disabled?: boolean;
  editorRef?: React.MutableRefObject<MonacoEditor | null>;
  onBlur?: () => void;
  onChange: (value: string) => void;
  onMount?: (editor: MonacoEditor, monaco: MonacoType) => Promise<void> | void;
  propSlots?: {
    editor: {
      colors?: Record<string, string>;
    };
  };
  value: string;
}

export const CodeEditor = forwardRef<MonacoEditor, Props>(
  ({ disabled, onBlur, onChange, onMount, options = {}, propSlots, value, ...props }, ref) => {
    const { palette } = useTheme();
    const [isLoading, setIsLoading] = useState(true);
    const [monacoInstance, setMonacoInstance] = useState<MonacoType | null>(null);

    const initMonaco = useCallback(async () => {
      try {
        const [monaco, { loader }] = await Promise.all([
          import('monaco-editor'),
          import('@monaco-editor/react'),
          import('monaco-editor/esm/vs/editor/editor.worker?worker'),
        ]);

        loader.config({ monaco });

        setMonacoInstance(monaco);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to initialize Monaco:', error);
      }
    }, []);

    useEffect(() => {
      initMonaco();
    }, [initMonaco]);

    const handleEditorMount: OnMount = useCallback(
      async (editor, monaco) => {
        if (ref) {
          if (typeof ref === 'function') {
            ref(editor);
          } else {
            ref.current = editor;
          }
        }

        if (onBlur) {
          editor.onDidBlurEditorWidget(onBlur);
        }

        if (monacoInstance?.editor) {
          monacoInstance.editor.defineTheme('custom-theme', {
            base: 'vs-dark',
            colors: {
              'editor.background':
                propSlots?.editor?.colors?.['editor.background'] ?? palette.background.paper,
              'editor.foreground':
                propSlots?.editor?.colors?.['editor.foreground'] ?? palette.text.primary,
            },
            inherit: true,
            rules: [],
          });
          monacoInstance.editor.setTheme('custom-theme');
        }

        if (onMount) {
          await onMount(editor, monaco);
        }

        setIsLoading(false);
      },
      [
        monacoInstance,
        onMount,
        palette.background.paper,
        palette.text.primary,
        ref,
        onBlur,
        propSlots,
      ],
    );

    if (!monacoInstance) {
      return <Loader />;
    }

    return (
      <Stack
        height="100%"
        minHeight={0}
        sx={{
          position: 'relative',
          visibility: isLoading ? 'hidden' : 'visible',
        }}
      >
        {isLoading && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              bottom: 0,
              left: 0,
              position: 'absolute',
              right: 0,
              top: 0,
            }}
          >
            <Loader />
          </Stack>
        )}
        <Editor
          {...props}
          loading={<Loader />}
          onChange={(value) => onChange(value ?? '')}
          onMount={handleEditorMount}
          options={{
            ...options,
            automaticLayout: true,
            readOnly: disabled,
            scrollbar: {
              ...options.scrollbar,
              alwaysConsumeMouseWheel: false,
            },
          }}
          value={value}
        />
      </Stack>
    );
  },
);
